import React, { memo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import D_Number from '@utils/number';
import { Tooltip } from 'antd';
import classnames from 'classnames';
import useTheme from '@hooks/useTheme';

const LABEL_STYLE = {
  'font-family': 'HelveticaNeue, Arial',
  'font-size': '0.875rem',
  color: '#333333 !important',
};
const DEFAULT_COLOR = '#B3DCF9';
const COMBINED_COLOR = '#0081E9';
const OTHERS_COLOR = '#FFB600';

const getCompAvg = (records) => {
  const compRecords = records.slice(3);
  return compRecords.reduce(
    ({ avg, count }, e) => {
      if (e.value == null || e.value === '') {
        return { avg, count };
      }
      return {
        avg: (avg * count + parseFloat(e.value)) / (count + 1),
        count: count + 1,
      };
    },
    { avg: null, count: 0 },
  ).avg;
};

const sgaChartOption = {
  chart: {
    type: 'column',
    height: 260,
    width: 320,
    marginBottom: 25,
  },
  title: {
    text: '',
    align: 'left',
    style: {
      'font-family': ' HelveticaNeue-Medium, Arial',
      'font-size': '1rem',
      color: '#DBDBDB !important',
    },
    margin: 30,
  },
  colors: ['#0081E9'],
  xAxis: [
    {
      labels: {
        enabled: false,
      },
      lineWidth: 0,
      tickmarkPlacement: 'on',
    },
    {
      opposite: true,
      min: 0,
      max: 5,
      labels: {
        align: 'right',
        reserveSpace: true,
        style: LABEL_STYLE,
        formatter() {
          if (this.value === '-') {
            return this.value;
          }
          return D_Number.formatPercentage(this.value / 100, 1);
        },
      },
      lineWidth: 0,
      tickmarkPlacement: 'on',
    },
  ],
  yAxis: [
    {
      title: {
        text: undefined,
      },
      gridLineWidth: 0,
      labels: {
        formatter() {
          return '';
        },
        style: {
          'font-family': 'HelveticaNeue, Arial',
          'font-size': '0.875rem',
          color: '#333333 !important',
        },
      },
      plotLines: [
        {
          id: 'plotline-0',
          color: '#7D7D7D',
          width: 1,
          value: 0,
          // value: 20,
          dashStyle: 'solid',
        },
        {
          className: 'comp-avg-line',
          id: 'plotline-1',
          color: '#E0A000',
          width: 2,
          value: 20,
          label: {
            formatter() {
              return `<span>Comp Average</span><br/><span>${D_Number.formatPercentage(
                this.options.value / 100,
                1,
              )}</span>`;
            },
            rotation: 0,
            style: {
              'font-family': 'HelveticaNeue, Arial',
              color: '#E0A000',
              fontWeight: 'bold',
              width: 100,
              verticalAlign: 'middle',
              textAlign: 'center',
              textAnchor: 'middle',
            },
            // x: -44,
            y: 235,
          },
          dashStyle: 'longdash',
          events: undefined,
        },
      ],
    },
  ],
  series: [],
  legend: {
    enabled: false,
  },
  credits: {
    enabled: false,
  },
  tooltip: {
    enabled: false,
  },
  plotOptions: {
    bar: {
      borderWidth: 0,
      pointWidth: 9.3,
      borderRadius: 5,
    },
  },
};

const getBarColor = (index) => {
  if (index < 2) {
    return DEFAULT_COLOR;
  }
  if (index < 3) {
    return COMBINED_COLOR;
  }
  return OTHERS_COLOR;
};

const CompanyNames = ({ names }) => (
  <div className="company-name-label-group">
    {names.map((n, i) => (
      <Tooltip className="company-name-label" key={i} title={n}>
        {n}
      </Tooltip>
    ))}
  </div>
);

const SgaChart = ({ chartDataSrc, chosenField }) => {
  const { theme } = useTheme();
  const isLightMode = theme === 'light';
  const formatValuesInChart = (values) =>
    values
      .map((v) => {
        const res = { name: v.name };
        let name;
        if (v.name == null || v.name === '') {
          name = '';
        }
        if (v.value == null) {
          return Object.assign({}, res, { value: null, name });
        }
        return Object.assign({}, res, { value: parseFloat(v.value), name });
      })
      // .filter(isNotJunk)
      .reduce(
        (acc, e, i) =>
          Array.prototype.concat([], acc, [
            { name: e.name, y: e.value, color: getBarColor(i) },
          ]),
        [],
      );

  const applyData = (chartOption) =>
    Object.assign(chartOption, {
      series: [
        {
          name: chosenField,
          data: formatValuesInChart(chartDataSrc),
          type: 'bar',
        },
        {
          name: chosenField,
          type: 'line',
          xAxis: 1,
        },
      ],
    });

  const genXAxisName = (chartOption) => {
    chartOption.xAxis[0].categories = chartDataSrc.map((r) => r.name);
    chartOption.xAxis[1].categories = chartDataSrc.map((r) =>
      r.value == null ? '-' : r.value,
    );
  };

  const passValueToPlotline = (option) => {
    option.yAxis[0].plotLines[1].value = getCompAvg(chartDataSrc);
  };

  const option = Object.assign({}, sgaChartOption);

  applyData(option);
  genXAxisName(option);
  passValueToPlotline(option);

  return (
    <div className="chart-wrapper">
      <div
        className={classnames('chart-and-names', {
          'highcharts-dark': !isLightMode,
          'highcharts-light': isLightMode,
        })}
        id="tooltip-anchor"
      >
        <CompanyNames names={chartDataSrc.map((d) => d.name)} />
        <HighchartsReact
          highcharts={Highcharts}
          constructorType="chart"
          options={option}
        />
      </div>
    </div>
  );
};

export default memo(SgaChart);
